/**
 * ServiceFarmsList — Granjas / dominios (estándar de listados del CMDB).
 *
 * Antes: tabla propia (`wol-table`), diálogo de borrado propio con
 * `window.confirm` y la ficha abriendo **directamente en edición**.
 *
 * Ahora: `ListPage` con buscador, filtros, columnas, vistas y export; la ficha
 * entra en **lectura** (con botón Editar) y el borrado usa el diálogo único.
 *
 * Botones propios:
 *  · **Ver sus nodos** — diálogo con los miembros de la granja, su rol, su host
 *    y su estado (el listado ya pide `include_members=1`, así que sale de la
 *    propia fila sin pedir nada más).
 *  · **Ver el servicio** — a la ficha del servicio en el Catálogo.
 *  · **Ver el Diseñador** — al grafo de ese servicio.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ServerStackIcon,
  ListBulletIcon,
  Squares2X2Icon,
  UserGroupIcon,
  ShareIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import ServiceFarmDetail from './ServiceFarmDetail';
import { useListView } from '../views/ViewsContext';
import { useAutoOpen } from '../hooks/useAutoOpen';
import api from '../services/api';

const STATUS_META = {
  active:      { label: 'Activa',        badge: 'list-badge--on' },
  degraded:    { label: 'Degradada',     badge: 'list-badge--warn' },
  maintenance: { label: 'Mantenimiento', badge: 'list-badge--warn' },
  offline:     { label: 'Caída',         badge: 'list-badge--off' },
};

const MEMBER_STATUS_META = {
  online:      { label: 'Online',        badge: 'list-badge--on' },
  offline:     { label: 'Offline',       badge: 'list-badge--off' },
  maintenance: { label: 'Mantenimiento', badge: 'list-badge--warn' },
};

const EMPTY_FARM = {
  name: '', service_id: '', farm_type: 'other', fqdn: '',
  functional_level: '', status: 'active', description: '', notes: '',
};

export default function ServiceFarmsList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [farms, setFarms]       = useState([]);
  const [meta, setMeta]         = useState({ farm_types: [], statuses: [] });
  const [services, setServices] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');

  const [filterType, setFilterType]       = useState('');
  const [filterStatus, setFilterStatus]   = useState('');
  const [filterService, setFilterService] = useState('');
  const [filterNodes, setFilterNodes]     = useState('');
  const [filterLevel, setFilterLevel]     = useState('');
  const [groupMode, setGroupMode]         = useState('');

  const [selected, setSelected]       = useState(null);
  const [initialMode, setInitialMode] = useState('edit');
  const [hlId, setHlId]               = useState(null);
  const [membersOf, setMembersOf]     = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [metaResp, list, svcs] = await Promise.all([
        api.getServiceFarmsMeta(companyId),
        api.getServiceFarms(companyId, { include_members: '1' }),
        api.getServices(companyId),
      ]);
      setMeta(metaResp || { farm_types: [], statuses: [] });
      setFarms(Array.isArray(list) ? list : []);
      setServices(Array.isArray(svcs) ? svcs : (svcs.services || []));
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las granjas');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ?destacar=<id>[&abrir=1] */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !farms.length) return undefined;
    const id = Number(raw);
    const target = farms.find(f => f.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setInitialMode('view'); setSelected(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`farm-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, farms, setSearchParams]);

  /* ── ficha ──────────────────────────────────────────────────────────────── */
  const openView   = (f) => { setInitialMode('view');   setSelected(f); };
  const openEdit   = (f) => { setInitialMode('edit');   setSelected(f); };
  const openCreate = ()  => { setInitialMode('create'); setSelected({ ...EMPTY_FARM }); };
  const closeForm  = ()  => { setSelected(null); load(); };
  useAutoOpen(farms, openEdit);

  const handleDelete = async (farm) => {
    try {
      await api.deleteServiceFarm(farm.id, companyId);
      await load();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar');
    }
  };

  /* ── filtrado ───────────────────────────────────────────────────────────── */
  const farmTypeLabel = useCallback(
    (type) => (meta.farm_types || []).find(ft => ft.value === type)?.label || type,
    [meta.farm_types],
  );

  const typeOptions = useMemo(() => {
    const seen = new Map();
    farms.forEach(f => { if (f.farm_type) seen.set(f.farm_type, farmTypeLabel(f.farm_type)); });
    (meta.farm_types || []).forEach(ft => { if (!seen.has(ft.value)) seen.set(ft.value, ft.label); });
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [farms, meta.farm_types, farmTypeLabel]);

  const levelOptions = useMemo(() => {
    const levels = [...new Set(farms.map(f => f.functional_level).filter(Boolean))]
      .sort((a, b) => String(a).localeCompare(String(b), 'es', { numeric: true }));
    return levels.map(l => ({ value: l, label: l }));
  }, [farms]);

  const serviceOptions = useMemo(() => {
    const seen = new Map();
    farms.forEach(f => { if (f.service_id) seen.set(String(f.service_id), f.service_name || `#${f.service_id}`); });
    return [...seen.entries()]
      .sort((a, b) => a[1].localeCompare(b[1], 'es'))
      .map(([value, label]) => ({ value, label }));
  }, [farms]);

  const memberCount = (f) => (f.members || []).length;

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return farms.filter((f) => {
      if (filterType && f.farm_type !== filterType) return false;
      if (filterStatus && (f.status || 'active') !== filterStatus) return false;
      if (filterService && String(f.service_id || '') !== String(filterService)) return false;
      if (filterNodes === 'with' && memberCount(f) === 0) return false;
      if (filterNodes === 'without' && memberCount(f) > 0) return false;
      if (filterLevel && String(f.functional_level || '') !== String(filterLevel)) return false;
      if (!term) return true;
      return [
        f.name, f.fqdn, f.description,
        f.service_name, farmTypeLabel(f.farm_type),
        (f.members || []).map(m => m.server_name || m.vm_name || m.ci_name).filter(Boolean).join(' '),
      ].filter(Boolean).join(' ').toLowerCase().includes(term);
    });
  }, [farms, search, filterType, filterStatus, filterService, filterNodes, filterLevel, farmTypeLabel]);

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: tr('farm.name', 'Nombre'),
        sortable: true,
        render: (f) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>{f.name}</span>
            {f.description && <span className="list-hint">{f.description}</span>}
          </span>
        ),
      },
      {
        key: 'farm_type',
        label: tr('farm.type', 'Tipo'),
        sortable: true,
        sortValue: (f) => f.farm_type || '',
        render: (f) => <span className="list-badge">{farmTypeLabel(f.farm_type) || dash}</span>,
      },
      {
        key: 'service',
        label: tr('farm.service', 'Servicio'),
        sortable: true,
        sortValue: (f) => f.service_name || '',
        render: (f) => (f.service_name
          ? (
            <button
              type="button"
              className="list-badge"
              style={{ cursor: 'pointer' }}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/services?destacar=${f.service_id}&abrir=1`);
              }}
              title={tr('farm.view_service', 'Ver el servicio al que sirve')}
            >
              {f.service_name}
            </button>
          )
          : dash),
      },
      {
        key: 'fqdn',
        label: 'FQDN',
        sortable: true,
        render: (f) => (f.fqdn ? <span className="list-code">{f.fqdn}</span> : dash),
      },
      {
        key: 'functional_level',
        label: tr('farm.level', 'Nivel funcional'),
        sortable: true,
        render: (f) => (f.functional_level || dash),
      },
      {
        key: 'members',
        label: tr('farm.members', 'Nodos'),
        sortable: true,
        sortValue: (f) => memberCount(f),
        render: (f) => {
          const count = memberCount(f);
          const online = (f.members || []).filter(m => m.status === 'online').length;
          return (
            <button
              type="button"
              className="list-badge"
              style={{ cursor: count ? 'pointer' : 'default' }}
              disabled={!count}
              onClick={(event) => { if (!count) return; event.stopPropagation(); setMembersOf(f); }}
              title={tr('farm.view_members', 'Ver sus nodos')}
            >
              {count}{count ? ` · ${online} online` : ''}
            </button>
          );
        },
      },
      {
        key: 'status',
        label: tr('ipam.status', 'Estado'),
        sortable: true,
        sortValue: (f) => f.status || 'active',
        render: (f) => {
          const meta_ = STATUS_META[f.status] || { label: f.status || '—', badge: '' };
          return <span className={`list-badge ${meta_.badge}`}>{meta_.label}</span>;
        },
      },
      {
        key: 'notes',
        label: tr('description', 'Notas'),
        render: (f) => f.notes || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, navigate, farmTypeLabel]);

  const defaultVisible = ['name', 'farm_type', 'service', 'fqdn', 'members', 'status'];

  const filters = [
    {
      key: 'type', label: tr('farm.type', 'Tipo'), allLabel: tr('farm.type_all', 'Todos los tipos'),
      value: filterType, onChange: setFilterType, options: typeOptions,
    },
    {
      key: 'status', label: tr('ipam.status', 'Estado'), allLabel: tr('ipam.status_all', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: [
        { value: 'active', label: STATUS_META.active.label },
        { value: 'degraded', label: STATUS_META.degraded.label },
        { value: 'maintenance', label: STATUS_META.maintenance.label },
        { value: 'offline', label: STATUS_META.offline.label },
      ],
    },
    {
      key: 'service', label: tr('farm.service', 'Servicio'), allLabel: tr('farm.service_all', 'Cualquier servicio'),
      value: filterService, onChange: setFilterService, options: serviceOptions,
    },
    {
      key: 'nodes', label: tr('farm.members', 'Nodos'), allLabel: tr('farm.members_all', 'Con o sin nodos'),
      value: filterNodes, onChange: setFilterNodes,
      options: [
        { value: 'with', label: tr('farm.members_with', 'Con nodos') },
        { value: 'without', label: tr('farm.members_without', 'Sin nodos') },
      ],
    },
    {
      key: 'level', label: tr('farm.level', 'Nivel funcional'), allLabel: tr('farm.level_all', 'Cualquier nivel'),
      value: filterLevel, onChange: setFilterLevel, options: levelOptions,
    },
    {
      key: 'group', label: tr('farm.group', 'Agrupar por'), allLabel: tr('farm.group_none', 'Sin agrupar'),
      value: groupMode, onChange: setGroupMode,
      options: [
        { value: 'type', label: tr('farm.type', 'Tipo') },
        { value: 'service', label: tr('farm.service', 'Servicio') },
      ],
    },
  ];

  const groupBy = useMemo(() => {
    if (groupMode === 'type') return (f) => farmTypeLabel(f.farm_type) || tr('list.no_value', 'Sin valor');
    if (groupMode === 'service') return (f) => f.service_name || tr('farm.no_service', 'Sin servicio');
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupMode, farmTypeLabel]);

  const subtitle = `${farms.length} ${tr('farm.plural', 'granjas')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtradas')}`;

  const exportRows = useMemo(() => filtered.map(f => ({
    'Nombre': f.name || '',
    'Tipo': farmTypeLabel(f.farm_type) || '',
    'Servicio': f.service_name || '',
    'FQDN': f.fqdn || '',
    'Nivel funcional': f.functional_level || '',
    'Nodos': memberCount(f),
    'Nodos online': (f.members || []).filter(m => m.status === 'online').length,
    'Nodos (detalle)': (f.members || [])
      .map(m => `${m.role || 'sin rol'}: ${m.server_name || m.vm_name || m.ci_name || ''}`)
      .filter(Boolean).join(' · '),
    'Estado': (STATUS_META[f.status] || {}).label || f.status || '',
    'Descripción': f.description || '',
    'Notas': f.notes || '',
  })), [filtered, farmTypeLabel]);

  /** Tarjetas: lo esencial de la granja, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((f) => {
          const st = STATUS_META[f.status] || { label: f.status || '—', badge: '' };
          return (
            <div
              key={f.id}
              id={`farm-card-${f.id}`}
              className={`list-card${f.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openView(f)}
              onKeyDown={(event) => { if (event.key === 'Enter') openView(f); }}
              title={`${tr('action.view', 'Ver')} ${f.name}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">{f.name}</div>
                  {f.fqdn && <span className="list-code">{f.fqdn}</span>}
                </div>
                <span className={`list-badge ${st.badge}`}>{st.label}</span>
              </div>

              <div className="list-card-meta">
                <span className="list-badge">{farmTypeLabel(f.farm_type)}</span>
                {f.service_name && <span>{f.service_name}</span>}
                {f.functional_level && <span className="list-hint">{tr('farm.level', 'Nivel')} {f.functional_level}</span>}
                <span className="list-badge">{memberCount(f)} {tr('farm.members', 'nodos')}</span>
              </div>

              <div className="list-card-actions">{rowActions(f)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  const FARM_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada granja'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId]);
  useListView('service_farms', 'list');

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          {tr('farm.select_company', 'Selecciona una empresa para ver las granjas.')}
        </div>
      </div>
    );
  }

  return (
    <>
      {selected !== null && (
        <ServiceFarmDetail
          farm={selected}
          companyId={companyId}
          meta={meta}
          services={services}
          initialMode={initialMode}
          onClose={closeForm}
          onSaved={(creado) => {
            load();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setInitialMode('edit'); setSelected(creado); }
          }}
        />
      )}

      <div style={{ display: selected !== null ? 'none' : undefined }}>
        <ListPage
          icon={ServerStackIcon}
          title={tr('farm.title', 'Granjas / dominios')}
          subtitle={subtitle}

          onCreate={openCreate}
          createLabel={tr('farm.new', 'Nueva granja')}
          createTitle={tr('farm.new_hint', 'Granja de servidores o dominio (AD, balanceo, cluster…)')}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('farm.search', 'Buscar por nombre, FQDN, servicio, nodo…')}
          filters={filters}

          exportRows={exportRows}
          exportFileBase="granjas"
          exportSheetName="Granjas"

          columns={columns}
          columnStorageKey="cmdb_cols_service_farms"
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(f) => f.id}
          rowId={(f) => `farm-row-${f.id}`}
          rowClassName={(f) => (f.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={openView}
          rowTitle={(f) => `${tr('action.view', 'Ver')} ${f.name}`}
          groupBy={groupBy}

          views={FARM_VIEWS}
          listKey="service_farms"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando granjas…')}
          error={error}

          empty={{
            icon: '🏢',
            title: farms.length === 0
              ? tr('farm.empty', 'No hay granjas')
              : tr('msg.no_results', 'Ninguna granja coincide con los filtros'),
            subtitle: farms.length === 0
              ? tr('farm.empty_hint', 'Una granja agrupa los nodos que sirven a un servicio (o un dominio AD con sus controladores).')
              : undefined,
            action: farms.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={openCreate}>
                ＋ {tr('farm.new', 'Nueva granja')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (f) => (
              <>
                {/* Ver sus nodos */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!memberCount(f)}
                  onClick={() => setMembersOf(f)}
                  title={tr('farm.view_members', 'Ver sus nodos')}
                >
                  <UserGroupIcon />
                </button>
                {/* Ver el servicio */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!f.service_id}
                  onClick={() => navigate(`/services?destacar=${f.service_id}&abrir=1`)}
                  title={f.service_name
                    ? `${tr('farm.view_service', 'Ver el servicio al que sirve')}: ${f.service_name}`
                    : tr('farm.no_service', 'Sin servicio')}
                >
                  <WrenchScrewdriverIcon />
                </button>
                {/* Ver en el Diseñador */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!f.service_id}
                  onClick={() => navigate(`/services/designer?service=${f.service_id}`)}
                  title={tr('virt.view_in_designer', 'Ver en el Diseñador de servicios')}
                >
                  <ShareIcon />
                </button>
              </>
            ),
            onEdit: openEdit,
            onDelete: handleDelete,
            deleteTitle: tr('farm.delete_title', 'Eliminar granja'),
            deleteMessage: (f) => (
              <>
                {tr('virt.delete_confirm', '¿Seguro que quieres eliminar')} <strong>{f.name}</strong>?{' '}
                {memberCount(f) > 0
                  ? tr('farm.delete_used', `Se eliminarán también sus ${memberCount(f)} nodo(s).`)
                  : tr('ip.delete_warn', 'No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>

      {/* ══ Diálogo: nodos de la granja ══ */}
      {membersOf && (
        <Dialog
          title={`${tr('farm.members_of', 'Nodos de')} ${membersOf.name}`}
          icon={<UserGroupIcon style={{ width: 18, height: 18 }} />}
          maxWidth={680}
          onClose={() => setMembersOf(null)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setMembersOf(null)}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          <div>
            {(membersOf.members || []).map((m) => {
              const st = MEMBER_STATUS_META[m.status] || { label: m.status || '—', badge: '' };
              const ciType = m.ci_type || (m.vm_id ? 'virt_vm' : m.server_id ? 'server' : null);
              const ciId = m.ci_id || m.vm_id || m.server_id;
              const name = m.server_name || m.vm_name || m.ci_name;
              return (
                <div
                  key={m.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                    padding: '6px 0', borderBottom: '1px solid var(--border)',
                  }}
                >
                  <span style={{ minWidth: 90 }}>
                    {m.role ? <span className="list-badge">{m.role}</span> : <span className="list-hint">—</span>}
                  </span>
                  <span style={{ minWidth: 150 }}>
                    {name && ciType && ciId
                      ? (
                        <button
                          type="button"
                          className="list-badge"
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`${
                            ciType === 'virt_vm' ? '/virt/vms' : '/infrastructure/servers'
                          }?destacar=${ciId}&abrir=1`)}
                          title={tr('farm.view_host', 'Ver el nodo')}
                        >
                          {name}
                        </button>
                      )
                      : (name || <span className="list-hint">—</span>)}
                  </span>
                  {m.is_primary && <span className="list-badge list-badge--on">{tr('farm.primary', 'Primario')}</span>}
                  <span className={`list-badge ${st.badge}`} style={{ marginLeft: 'auto' }}>{st.label}</span>
                </div>
              );
            })}
            {(membersOf.members || []).length === 0 && (
              <p className="list-hint" style={{ margin: 0, textAlign: 'center' }}>
                {tr('farm.no_members', 'Sin nodos todavía.')}
              </p>
            )}
          </div>
          <p className="list-hint" style={{ marginTop: 10, marginBottom: 0 }}>
            {tr('farm.members_hint', 'Para añadir o quitar nodos, abre la ficha de la granja y pulsa Editar.')}
          </p>
        </Dialog>
      )}
    </>
  );
}
